/**
 * AI Insights Widget
 *
 * Renders a CMO-grade narrative breakdown of the team's social analytics:
 * a hero summary, prioritized action cards, optional trend chip and
 * content-type pills. Compact, collapsible, and supports "show more" so
 * long action lists stay digestible.
 *
 * Layout: summary + meta stay full-width. The recommendations grid renders
 * 1 column on mobile and 2 columns on sm+ so the action stack reads quickly
 * at any width without going taller than it needs to.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAIInsights, useGenerateAIInsights } from '@/hooks/useAnalyticsWidgets';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { focusSurface } from '@/components/ui/focusStyles';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';
import { DashboardCollapseButton } from './DashboardCollapseButton';
import { FeatureGate } from '@/components/gating/FeatureGate';
import { LockBadge } from '@/components/gating/LockBadge';
import { getFeatureCopy } from '@/components/gating/gatingCopy';
import { useLockedEntry } from '@/hooks/useLockedEntry';

const RECOMMENDATIONS_PREVIEW = 4;

// How often to re-check for a fresh insight while a generation job is queued.
const POLL_INTERVAL_MS = 4000;
// Give up waiting for the background job after this long and show a
// retry-able error instead of polling forever.
const POLL_TIMEOUT_MS = 60000;

type GenerationPhase = 'idle' | 'polling' | 'insufficient' | 'error';

/** Lara insights: the locked teaser replaces the widget when the plan does not include `lara_insights`. */
export function AIInsightsWidget() {
  const lock = useLockedEntry('lara_insights');
  const { t: tCommon } = useTranslation('common');
  // Really locked (same resolver as the gates): a slim one-row teaser in the widget's slot; click opens the upsell.
  // A genuinely empty but allowed state is filtered out upstream (Dashboard) exactly as before.
  if (lock.locked) {
    const title = getFeatureCopy(tCommon, 'lara_insights').title;
    return (
      <button
        type="button"
        onClick={lock.showUpsell}
        data-testid={lock.testId}
        aria-label={lock.ariaLabelFor(title)}
        className={cn(
          'w-full flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-primary-300',
          focusSurface
        )}
      >
        <Icon name={ICONS.MAGIC_WAND} size="md" color="text-primary-500" ariaHidden />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900 truncate">{title}</span>
          <span className="block text-xs font-medium text-primary-700 truncate">{lock.label}</span>
        </span>
        <LockBadge />
      </button>
    );
  }
  return (
    <FeatureGate feature="lara_insights" compact>
      <AIInsightsWidgetContent />
    </FeatureGate>
  );
}

function AIInsightsWidgetContent() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatRelativeDate } = useTimezone();
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [titleTooltipOpen, setTitleTooltipOpen] = useState(false);
  const [showAllRecs, setShowAllRecs] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('idle');
  const [insufficientInfo, setInsufficientInfo] = useState<{ postsFound: number; minRequired: number } | null>(
    null
  );

  const isPolling = generationPhase === 'polling';
  const { data: insights, isLoading } = useAIInsights(undefined, isPolling ? POLL_INTERVAL_MS : false);
  const generateMutation = useGenerateAIInsights();

  // Tracks the `generatedAt` of the active insight at the moment generation
  // was triggered, so polling can detect when a *new* insight has landed
  // (rather than just re-seeing the old one). A ref avoids stale closures
  // inside the polling effect/timeout.
  const pollBaselineRef = useRef<string | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  // Stop any in-flight timeout on unmount.
  useEffect(() => () => clearPollTimeout(), []);

  const cardChrome = 'relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm';
  const accentStrip = 'absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-coral-400';

  const activeInsight = selectedPlatform
    ? insights?.find((i) => i.platform === selectedPlatform)
    : insights?.[0];
  const payload = activeInsight?.insights;
  const hasContent = !!payload?.summary;
  const isGenerating = generateMutation.isPending || isPolling;

  // While polling, watch for a *fresh* insight (new generatedAt + content)
  // and stop as soon as one appears.
  useEffect(() => {
    if (!isPolling) return;
    const currentGeneratedAt = activeInsight?.generatedAt ?? null;
    const hasFreshContent = !!activeInsight?.insights?.summary && currentGeneratedAt !== pollBaselineRef.current;
    if (hasFreshContent) {
      clearPollTimeout();
      setGenerationPhase('idle');
    }
    // Only re-run when the polling flag or the polled data actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, activeInsight?.generatedAt, activeInsight?.insights?.summary]);

  const handleGenerate = () => {
    setInsufficientInfo(null);
    clearPollTimeout();
    pollBaselineRef.current = activeInsight?.generatedAt ?? null;

    generateMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === 'insufficient_data') {
          setGenerationPhase('insufficient');
          setInsufficientInfo({
            postsFound: result.postsFound ?? 0,
            minRequired: result.minRequired ?? 3,
          });
          return;
        }

        setGenerationPhase('polling');
        pollTimeoutRef.current = setTimeout(() => {
          setGenerationPhase((phase) => (phase === 'polling' ? 'error' : phase));
        }, POLL_TIMEOUT_MS);
      },
      onError: (error) => {
        setGenerationPhase('error');
        if (isGlobalGatingHandledError(error)) return;
        toast.error(formatApiError(error).message);
      },
    });
  };

  // ── Header (always visible) ────────────────────────────────────────────────
  const insightTitle = t('dashboard:dashboard_ai_insights_title', { defaultValue: 'CMO Insights' });
  const header = (
    <div className="flex items-center gap-2 px-4 py-4 sm:gap-3 sm:px-5 sm:py-5">
      <h3 className="sr-only text-lg font-bold text-gray-900">{insightTitle}</h3>
      <Tooltip content={insightTitle} side="bottom" open={titleTooltipOpen} onOpenChange={setTitleTooltipOpen}>
        <button
          type="button"
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 ${focusSurface}`}
          aria-label={insightTitle}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => {
            if (event.pointerType !== 'mouse') setTitleTooltipOpen((wasOpen) => !wasOpen);
          }}
        >
          <Icon name={ICONS.ROBOT} size="md" ariaHidden />
        </button>
      </Tooltip>
      {isGenerating && (
        <span className="min-w-0 truncate text-xs text-gray-500">
          {t('dashboard:dashboard_ai_insights_thinking', { defaultValue: 'Reading your last 30 days…' })}
        </span>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isGenerating}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 h-8 sm:h-8 px-2 sm:px-2.5 rounded-md text-xs font-medium transition-all',
            'border border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          aria-label={t('common:common_refresh', { defaultValue: 'Refresh' })}
        >
          <Icon
            name={ICONS.REFRESH}
            size="xs"
            ariaHidden
            className={isGenerating ? 'animate-spin' : ''}
          />
          <span className="hidden sm:inline">
            {isGenerating
              ? t('dashboard:common_generating', { defaultValue: 'Generating…' })
              : t('common:common_refresh', { defaultValue: 'Refresh' })}
          </span>
        </button>
        <DashboardCollapseButton collapsed={collapsed} onClick={() => setCollapsed((v) => !v)} />
      </div>
    </div>
  );

  // ── Insufficient-data / error banner (shown above whatever content state
  // is otherwise rendered, e.g. under the header on both the empty and
  // populated states) ────────────────────────────────────────────────────────
  const statusBanner =
    generationPhase === 'insufficient' && insufficientInfo ? (
      <div className="mx-4 sm:mx-5 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2">
        <Icon name={ICONS.WARNING} size="xs" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800 leading-relaxed">
          {t('dashboard:dashboard_ai_insights_insufficient', {
            defaultValue:
              'Not enough data yet — publish at least {{min}} posts in the last 30 days to get a CMO read.',
            min: insufficientInfo.minRequired,
          })}
        </p>
      </div>
    ) : generationPhase === 'error' ? (
      <div className="mx-4 sm:mx-5 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <Icon name={ICONS.ERROR} size="xs" color="text-red-600" ariaHidden className="mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-800 leading-relaxed">
            {t('dashboard:dashboard_ai_insights_error', {
              defaultValue: "Couldn't generate insights. Please try again.",
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          className="text-xs font-medium text-red-700 hover:text-red-900 flex-shrink-0 underline"
        >
          {t('common:try_again', { defaultValue: 'Try Again' })}
        </button>
      </div>
    ) : null;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={cardChrome}>
        <div className={accentStrip} />
        {header}
        {!collapsed && (
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <div className="animate-pulse space-y-2.5">
              <div className="h-3 bg-gray-100 rounded w-11/12" />
              <div className="h-3 bg-gray-100 rounded w-3/4" />
              <div className="space-y-1.5 pt-1">
                <div className="h-9 bg-gray-50 rounded-lg" />
                <div className="h-9 bg-gray-50 rounded-lg" />
                <div className="h-9 bg-gray-50 rounded-lg" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className={cardChrome}>
        <div className={accentStrip} />
        {header}
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!hasContent) {
    return (
      <div className={cardChrome}>
        <div className={accentStrip} />
        {header}
        {statusBanner}
        <div className="px-4 sm:px-5 pb-5 sm:pb-6">
          <div className="text-center py-5 sm:py-6 px-2">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-primary-50 to-soft-mint flex items-center justify-center ring-1 ring-primary-100">
              <Icon name={ICONS.SPARKLES} size="sm" color="text-primary-600" ariaHidden />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">
              {t('dashboard:dashboard_ai_insights_empty_title', { defaultValue: 'Get a CMO read on your data' })}
            </p>
            <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
              {t('dashboard:dashboard_ai_insights_empty_body', {
                defaultValue: 'A senior marketing strategist reviews your last 30 days and surfaces 3–5 prioritized actions.',
              })}
            </p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating}
              className={cn(
                'inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-sm font-medium transition-all',
                'bg-primary-500 text-primary-contrast hover:bg-primary-600 shadow-sm hover:shadow',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <Icon
                name={isGenerating ? ICONS.REFRESH : ICONS.SPARKLES}
                size="xs"
                ariaHidden
                className={isGenerating ? 'animate-spin' : ''}
              />
              {isGenerating
                ? t('dashboard:common_generating', { defaultValue: 'Generating…' })
                : t('dashboard:dashboard_ai_insights_generate_cta', { defaultValue: 'Generate insights' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Populated state ────────────────────────────────────────────────────────
  const allRecs = payload!.recommendations ?? [];
  const visibleRecs = showAllRecs ? allRecs : allRecs.slice(0, RECOMMENDATIONS_PREVIEW);
  const hasMoreRecs = allRecs.length > RECOMMENDATIONS_PREVIEW;

  // Hero summary block
  const summaryBlock = (
    <blockquote className="relative pl-3 border-l-2 border-primary-300">
      <p className="text-sm text-gray-800 leading-relaxed">{payload!.summary}</p>
    </blockquote>
  );

  // Recommendations stack
  const recommendationsBlock = allRecs.length > 0 ? (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 mb-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {t('dashboard:dashboard_ai_insights_recommendations', { defaultValue: 'Prioritized actions' })}
        </p>
        <div className="flex-1 h-px bg-gray-100" />
        {hasMoreRecs && !showAllRecs && (
          <span className="text-[11px] text-gray-400 font-medium">
            {t('dashboard:dashboard_ai_insights_count', {
              defaultValue: '{{shown}} of {{total}}',
              shown: RECOMMENDATIONS_PREVIEW,
              total: allRecs.length,
            })}
          </span>
        )}
      </div>
      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {visibleRecs.map((rec, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-100 bg-gray-50/60 hover:bg-white hover:border-primary-200 hover:shadow-sm transition-all"
          >
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500 text-primary-contrast text-[11px] font-semibold flex items-center justify-center mt-0.5">
              {idx + 1}
            </span>
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed flex-1">{rec}</p>
          </li>
        ))}
      </ol>
      {hasMoreRecs && (
        <button
          type="button"
          onClick={() => setShowAllRecs((v) => !v)}
          className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {showAllRecs
            ? t('dashboard:dashboard_ai_insights_show_less', { defaultValue: 'Show less' })
            : t('dashboard:dashboard_ai_insights_show_more', {
                defaultValue: 'Show {{count}} more',
                count: allRecs.length - RECOMMENDATIONS_PREVIEW,
              })}
          <Icon
            name={ICONS.ARROW_DOWN}
            size="xs"
            ariaHidden
            className={cn('transition-transform duration-200', showAllRecs ? 'rotate-180' : '')}
          />
        </button>
      )}
    </div>
  ) : null;

  // Meta block (content types + trend) — sits below the recommendations grid
  const metaBlock = (payload!.topPerformingContentTypes?.length || payload!.engagementTrends) ? (
    <div className="space-y-2.5 pt-2.5 border-t border-gray-100">
      {payload!.topPerformingContentTypes && payload!.topPerformingContentTypes.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mt-1">
            {t('dashboard:dashboard_ai_insights_top_types', { defaultValue: 'Working' })}
          </span>
          <div className="flex flex-wrap gap-1">
            {payload!.topPerformingContentTypes.map((type, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-700 border border-primary-100"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      )}
      {payload!.engagementTrends && (
        <div className="flex items-start gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mt-0.5 flex-shrink-0">
            {t('dashboard:dashboard_ai_insights_trend', { defaultValue: 'Trend' })}
          </span>
          <p className="text-xs text-gray-600 leading-relaxed">{payload!.engagementTrends}</p>
        </div>
      )}
    </div>
  ) : null;

  const footer = activeInsight?.generatedAt ? (
    <p className="text-[11px] text-gray-400 pt-1 flex items-center gap-1.5">
      <Icon name={ICONS.CLOCK} size="xs" ariaHidden />
      {t('dashboard:dashboard_ai_insights_updated', {
        defaultValue: 'Updated {{date}}',
        date: formatRelativeDate(activeInsight.generatedAt),
      })}
    </p>
  ) : null;

  return (
    <div className={cardChrome}>
      <div className={accentStrip} />
      {header}
      {statusBanner}

      {/* Platform pills (only when 2+ platforms have insights) */}
      {insights && insights.length > 1 && (
        <div className="px-4 sm:px-5 pb-2 flex flex-wrap gap-1">
          {insights.map((item) => {
            const isActive = (selectedPlatform || insights[0].platform) === item.platform;
            return (
              <button
                key={item.platform}
                type="button"
                onClick={() => setSelectedPlatform(item.platform)}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium rounded-full capitalize transition-colors',
                  isActive
                    ? 'bg-primary-500 text-primary-contrast'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {item.platform}
              </button>
            );
          })}
        </div>
      )}

      <div className="px-4 sm:px-5 pb-4 sm:pb-5">
        <div className="space-y-4">
          {summaryBlock}
          {recommendationsBlock}
          {metaBlock}
          {footer}
        </div>
      </div>
    </div>
  );
}
